import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRequireLogin } from '@/hooks/auth/useRequireLogin';
import { oidcAPI, AuthorizeParams, AuthorizeResult, ConsentPrompt } from '@/services/apis/oidcAPI';
import OidcConsentCard from '@/components/oidc/OidcConsentCard';
import { getErrorMessage } from '@/utils/errorUtils';

/**
 * OIDC 현관문 (#142)
 *
 * **서비스가 사용자를 보내는 주소가 이 화면이다** — 디스커버리 문서의
 * `authorization_endpoint` 가 여기를 가리킨다.
 *
 *     서비스 → (브라우저) /oidc/authorize?client_id=…&redirect_uri=…&state=…
 *               ├ 로그인 안 됨 → /login?redirect=<여기> → 로그인 → 돌아옴
 *               └ 로그인 됨   → 코드를 받아 서비스 콜백으로 이동
 *
 * 백엔드를 직접 가리킬 수 없다. 그쪽은 `Authorization` 헤더를 요구하고 브라우저는
 * 리다이렉트에 그 헤더를 싣지 못한다 — 그래서 사용자가 로그인 화면 대신 403 을 봤다.
 */

/** 서비스가 보낸 값을 그대로 모은다. 우리가 지어내지 않는다 */
const readParams = (search: string): AuthorizeParams | null => {
  const query = new URLSearchParams(search);
  const clientId = query.get('client_id');
  const redirectUri = query.get('redirect_uri');
  if (!clientId || !redirectUri) return null;

  const params: AuthorizeParams = { client_id: clientId, redirect_uri: redirectUri };
  const optional = [
    'response_type',
    'scope',
    'state',
    'nonce',
    'code_challenge',
    'code_challenge_method',
    // RFC 8707 — MCP 클라이언트가 보낸 자원. 빠뜨리면 토큰의 대상이 제품이 되지 않는다 (#216)
    'resource',
  ] as const;
  optional.forEach((key) => {
    const value = query.get(key);
    if (value) params[key] = value;
  });
  return params;
};

const OidcAuthorizePage: React.FC = () => {
  const loggedIn = useRequireLogin();
  const location = useLocation();
  const [error, setError] = useState('');
  // 동적(MCP) 앱은 코드 전에 사용자 동의를 받는다 (#216)
  const [consent, setConsent] = useState<ConsentPrompt | null>(null);
  // 코드는 한 번만 받는다. StrictMode 의 두 번 실행으로 코드를 버리지 않게 한다
  const started = useRef(false);

  const params = readParams(location.search);

  /** 동의가 필요하면 화면에 묻고, 아니면 콘솔을 벗어나 서비스로 나간다(브라우저 이동) */
  const follow = (result: AuthorizeResult) => {
    if ('consent_required' in result) {
      setConsent(result);
      return;
    }
    window.location.replace(result.redirect_to);
  };

  const decide = async (decision: 'approve' | 'deny') => {
    if (!params) return;
    try {
      follow(await oidcAPI.authorize({ ...params, consent: decision }));
    } catch (e) {
      setError(getErrorMessage(e, '앱 연결을 처리하지 못했습니다. 다시 시도해 주세요.'));
      throw e;
    }
  };

  useEffect(() => {
    if (!params) {
      setError('로그인 요청에 client_id 와 redirect_uri 가 있어야 합니다.');
      return;
    }

    // 로그인 안 되어 있으면 useRequireLogin 이 로그인 화면으로 보내고, 끝나면 이 주소로 돌아온다
    if (!loggedIn) return;

    if (started.current) return;
    started.current = true;

    oidcAPI
      .authorize(params)
      .then(follow)
      .catch((e) => {
        // 틀린 콜백 주소로는 보내지 않는다. 여기서 끝낸다
        setError(getErrorMessage(e, '로그인 연동을 시작할 수 없습니다.'));
        started.current = false;
      });
  }, [loggedIn, location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-lg shadow p-8 max-w-md w-full text-center">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-gray-900 mb-2">로그인 연동을 시작할 수 없습니다</h1>
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 mb-4">
              {error}
            </p>
            <p className="text-xs text-gray-500">
              이 서비스의 <strong>로그인 연동(client_id)</strong> 과 <strong>콜백 주소</strong> 가
              콘솔에 등록되어 있어야 합니다. 콜백 주소는 글자 하나까지 같아야 합니다.
            </p>
          </>
        ) : consent ? (
          <OidcConsentCard prompt={consent} onDecide={decide} />
        ) : (
          <>
            <div className="w-8 h-8 border-2 border-gray-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
            <h1 className="text-lg font-semibold text-gray-900 mb-1">로그인 중</h1>
            <p className="text-sm text-gray-500">
              GensApps 계정으로 서비스에 로그인합니다. 서비스에는 이메일 · 표시 이름 ·
              소속 고객사만 전달됩니다.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OidcAuthorizePage;
